/** Cliente HTTP da API. Erros seguem RFC 9457 (application/problem+json). */

export interface Problem {
  type?: string;
  title?: string;
  status: number;
  detail?: string;
  code?: string;
  issues?: unknown[];
  [k: string]: unknown;
}

export class ApiError extends Error {
  constructor(readonly status: number, readonly problem: Problem) {
    const issues = (problem.issues as { path?: string; message?: string }[] | undefined)?.slice(0, 4)
      .map((i) => (i.path ? `${i.path}: ${i.message}` : i.message)).join('; ');
    super(problem.detail || (issues ? `${problem.title ?? 'Dados inválidos'}: ${issues}` : problem.title) || `Erro ${status}`);
  }
}

type HeaderProvider = () => Promise<Record<string, string>>;
let authHeaders: HeaderProvider = async () => ({});
/** 401 da API, com o problema (ex.: code REAUTH_REQUIRED = passou do prazo do login). */
let onUnauthorized: (problem?: { code?: string; detail?: string }) => void = () => {};

export function configureApi(opts: { headers: HeaderProvider; onUnauthorized: (problem?: { code?: string; detail?: string }) => void }) {
  authHeaders = opts.headers;
  onUnauthorized = opts.onUnauthorized;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  ifMatch?: number | string;
  signal?: AbortSignal;
  query?: Record<string, string | number | boolean | undefined | null>;
}

export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const url = new URL(path, window.location.origin);
  for (const [k, v] of Object.entries(opts.query ?? {})) {
    if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
  }
  const headers: Record<string, string> = { Accept: 'application/json', ...(await authHeaders()) };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (opts.ifMatch !== undefined) headers['If-Match'] = `"${opts.ifMatch}"`;
  const res = await fetch(url, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    signal: opts.signal,
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? JSON.parse(text) : undefined;
  if (!res.ok) {
    if (res.status === 401) onUnauthorized(data);
    throw new ApiError(res.status, { status: res.status, ...(data ?? {}) });
  }
  return data as T;
}

export const errorMessage = (e: unknown) => (e instanceof Error ? e.message : String(e));

/** Salva no computador um conteúdo gerado na tela (ex.: CSV). */
export function saveBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = Object.assign(document.createElement('a'), { href: url, download: name });
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Baixa um arquivo da API (com o login), ex.: a exportação CSV. */
export async function downloadFile(path: string, name: string, query?: Record<string, string | undefined>) {
  const url = new URL(path, window.location.origin);
  for (const [k, v] of Object.entries(query ?? {})) if (v) url.searchParams.set(k, v);
  const res = await fetch(url, { headers: await authHeaders() });
  if (!res.ok) throw new ApiError(res.status, { status: res.status, title: `Erro ${res.status}` });
  saveBlob(await res.blob(), name);
}

/** Imagem da API que exige login (foto das pessoas): URL local do conteúdo. */
export async function blobUrl(path: string): Promise<string> {
  const res = await fetch(new URL(path, window.location.origin), { headers: await authHeaders() });
  if (!res.ok) throw new ApiError(res.status, { status: res.status, title: `Erro ${res.status}` });
  return URL.createObjectURL(await res.blob());
}

/**
 * Envia um arquivo como corpo cru (application/octet-stream), com o andamento em % (o fetch não informa o envio; o
 * XMLHttpRequest sim). Devolve a resposta JSON da API.
 */
export async function uploadFile<T>(path: string, file: File, query: Record<string, string>, onProgress?: (pct: number) => void): Promise<T> {
  const url = new URL(path, window.location.origin);
  for (const [k, v] of Object.entries(query)) url.searchParams.set(k, v);
  const headers = await authHeaders();
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress?.(Math.round((e.loaded / e.total) * 100)); };
    xhr.onload = () => {
      const data = xhr.responseText ? (() => { try { return JSON.parse(xhr.responseText); } catch { return undefined; } })() : undefined;
      if (xhr.status >= 200 && xhr.status < 300) resolve(data as T);
      else {
        if (xhr.status === 401) onUnauthorized(data);
        reject(new ApiError(xhr.status, { status: xhr.status, title: xhr.status === 413 ? 'Arquivo grande demais' : `Erro ${xhr.status}`, ...(data ?? {}) }));
      }
    };
    xhr.onerror = () => reject(new ApiError(0, { status: 0, title: 'A conexão caiu durante o envio. Tente de novo.' }));
    xhr.send(file);
  });
}
